import React, { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useDispatch, useSelector } from "react-redux";
import { Box, Button, Grid, Pagination, TablePagination } from "@mui/material";

import newSortBy from "../../../../utils/newSortBy";
import { ISortBy } from "../../../../types";
import usePagination from "../../../../hooks/usePagination";
import { useSearchParams } from "../../../../hooks/useSearchParams";
import ExtendOrCreateTrader from "../ExtendOrCreateTrader";
import { fundedComplianceActions } from "../../../../reducers/FundedCompliance";
import { appendSearchParamsToQuery } from "../../../ComplianceSearch/utils";
import { useAdminProviderDataContext } from "../../../../context";
import { Table, FilterAutocomplete, StatusColorLegend } from "../../../../components/ui";
import { FUNDED_COMPLIANCE_API_URLS, FundedComplianceAPI } from "../../../../api/fundedCompliance";
import { FUNDED_COMPLIANCE_FILTERS, TRADERS_DB_LIST_COLUMNS } from "./constants";

const TradersDbList = (): React.ReactElement => {
  const dispatch = useDispatch();
  const { accountGroups } = useSelector((state) => state.fundedCompliance);

  const [isOpen, setIsOpen] = useState(false);
  const [sortBy, setSortBy] = useState<ISortBy>({ by: "id", order: false });

  const { activeTab = "", getTableState, setTableState } = useSearchParams();

  const tableState = getTableState(activeTab, "traders-db-list", {
    page: 0,
    limit: 20,
    name: "",
    email: "",
    compliance_entries_id: "",
  });

  const pagination = usePagination(tableState.limit, tableState.page);

  const {
    user: {
      allowed_features: { access_edit_compliance_entries },
    },
  } = useAdminProviderDataContext();

  const [search, setSearch] = useState<{
    compliance_entries_id: string;
    name: string;
    email: string;
  }>({
    compliance_entries_id: tableState.compliance_entries_id,
    name: tableState.name,
    email: tableState.email,
  });

  const { isError, error } = useQuery({
    queryKey: [
      FUNDED_COMPLIANCE_API_URLS.tradersDbList,
      "tradersDbList",
      Object.values(search).join(", "),
      sortBy,
      pagination.page,
      pagination.limit,
    ],
    queryFn: async ({ signal }) => {
      const query = new URLSearchParams();

      appendSearchParamsToQuery(search, query);
      query.append("sortBy", sortBy.by);
      query.append("order", sortBy.order ? "ASC" : "DESC");
      query.append("page", String(pagination.page));
      query.append("limit", String(pagination.limit));

      dispatch(fundedComplianceActions.fetchAccountGroupsRequest());
      const response = await FundedComplianceAPI.getTradersDbListRequest(query.toString(), signal);

      dispatch(fundedComplianceActions.fetchAccountGroupsSuccess(response.data));
      pagination.setCount(response.data.count);

      return response.data;
    },
  });

  useEffect(() => {
    if (isError && error) {
      dispatch(fundedComplianceActions.fetchAccountGroupsFailed());
    }
  }, [isError, error]);

  useEffect(() => {
    setTableState(activeTab, "traders-db-list", {
      page: pagination.page,
      limit: pagination.limit,
      name: search.name,
      email: search.email,
      compliance_entries_id: search.compliance_entries_id,
    });
  }, [
    pagination.page,
    pagination.limit,
    search.name,
    search.email,
    search.compliance_entries_id,
    activeTab,
  ]);

  const handleSortBy = (by: string) => newSortBy({ by, sortBy, setSortBy });

  const handleChange = (event: React.ChangeEvent<unknown>, value: number) => {
    pagination.setPage(value - 1);
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    pagination.setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    pagination.setLimit(+event.target.value);
    pagination.setPage(0);
  };

  return (
    <div>
      <ExtendOrCreateTrader isOpen={isOpen} setIsOpen={setIsOpen} />
      <Grid container spacing={2}>
        <Grid item xs={access_edit_compliance_entries ? 10 : 12}>
          <FilterAutocomplete
            search={search}
            setSearch={setSearch}
            filters={FUNDED_COMPLIANCE_FILTERS}
          />
        </Grid>

        {access_edit_compliance_entries && (
          <Grid item xs={2}>
            <Button fullWidth onClick={() => setIsOpen(true)} sx={{ height: "100%" }}>
              Create new entry
            </Button>
          </Grid>
        )}

        <Grid item xs={12} style={{ display: "flex", justifyContent: "flex-end" }}>
          <StatusColorLegend
            legend={{
              outer_emails: {
                color: "#fec5bb",
                name: "Missed account in envoy",
              },
            }}
          />
        </Grid>
      </Grid>

      <Table
        columns={TRADERS_DB_LIST_COLUMNS}
        data={accountGroups.data.rows}
        loading={accountGroups.isLoading}
        extractKey={(row) => row.id}
        theadTitle="Traders DB List"
        csv_table_name="Traders DB List"
        handleSortBy={handleSortBy}
        sortBy={sortBy}
      />

      <Box display="flex" justifyContent="space-between">
        <TablePagination
          sx={{
            "& .MuiTablePagination-spacer": {
              flex: "1 auto",
            },
          }}
          rowsPerPageOptions={[5, 10, 20, 50, 100]}
          component="div"
          count={Math.ceil(pagination.count / +pagination.limit)}
          rowsPerPage={+pagination.limit}
          page={pagination.count <= 0 ? 0 : pagination.page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          ActionsComponent={() => (
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Pagination
                count={Math.ceil(pagination.count / +pagination.limit)}
                page={pagination.page + 1}
                onChange={handleChange}
                showFirstButton
                showLastButton
                size="small"
              />
            </Box>
          )}
        />
      </Box>
    </div>
  );
};

export default TradersDbList;
